/**
 * Un campo completo: etiqueta + control + ayuda + error.
 *
 * El formulario no sabe que widget le toca a cada campo; pide este componente y
 * el registro resuelve. Todo lo que hace falta para que un campo sea accesible
 * vive aca y en un solo lugar: `htmlFor`, `aria-describedby` con la ayuda y el
 * error, y el error anunciado con `role="alert"`.
 *
 * Los booleanos se dibujan al reves — control primero, etiqueta despues — porque
 * "☑ Es una empresa" se lee como una frase y "Es una empresa: ☑" no.
 */

import clsx from 'clsx';

import { getFieldInput, resolveWidgetName, widgetUsaHermanos } from '@/components/fields/registry';
import type { CamposHermanos, FieldContext } from '@/components/fields/types';
import type { FieldMeta, FieldValue } from '@/lib/types';

export interface FieldControlProps {
  name: string;
  meta: FieldMeta;
  value: FieldValue;
  onChange: (valor: FieldValue) => void;
  /** Se suma al `readonly` de la metadata: permisos, estado del registro, etc. */
  readonly?: boolean;
  error?: string | null;
  context: FieldContext;
  /** Prefijo del id, para que dos formularios en la misma pagina no colisionen. */
  idPrefix?: string;
  /**
   * Aclaracion que agrega la PANTALLA, no la metadata.
   *
   * `meta.help` lo escribe el modulo y habla del campo en abstracto («Neto ya
   * facturado, sin contar las facturas anuladas»). Esto otro lo deduce el
   * formulario mirando los valores que tiene enfrente —«Es la suma de Neto +
   * Impuestos»— y por eso no puede vivir en el schema: depende del registro.
   * Se dibuja igual que la ayuda y entra en el `aria-describedby`.
   */
  hint?: string | null;
  /**
   * Los otros campos del formulario, para los widgets que los necesitan.
   *
   * Se pasa SIEMPRE desde `FormView` y se entrega SOLO a los widgets que lo
   * declararon en el registro (`widgetUsaHermanos`). El filtro vive acá y no en
   * el formulario para que la lista blanca sea una sola y esté al lado del
   * registro que la define.
   */
  siblings?: CamposHermanos;
}

export function FieldControl({
  name,
  meta,
  value,
  onChange,
  readonly = false,
  error = null,
  context,
  idPrefix = 'campo',
  hint = null,
  siblings,
}: FieldControlProps) {
  const id = `${idPrefix}-${name}`;
  const idAyuda = `${id}-ayuda`;
  const idAclaracion = `${id}-aclaracion`;
  const idError = `${id}-error`;
  const Widget = getFieldInput(meta);
  const widget = resolveWidgetName(meta);

  const soloLectura = readonly || meta.readonly;
  const requerido = meta.required && !soloLectura;
  const ayuda = meta.help ?? '';
  const aclaracion = hint ?? '';
  const describedBy = [
    ayuda === '' ? null : idAyuda,
    aclaracion === '' ? null : idAclaracion,
    error === null ? null : idError,
  ]
    .filter((parte): parte is string => parte !== null)
    .join(' ');

  const control = (
    <Widget
      id={id}
      name={name}
      meta={meta}
      value={value}
      onChange={onChange}
      readonly={soloLectura}
      required={requerido}
      invalid={error !== null}
      context={context}
      {...(describedBy === '' ? {} : { describedBy })}
      {...(siblings !== undefined && widgetUsaHermanos(widget) ? { siblings } : {})}
    />
  );

  const etiqueta = (
    <label
      id={`${id}-label`}
      htmlFor={id}
      className={clsx('text-sm font-medium text-fg-muted', soloLectura && 'text-fg-faint')}
    >
      {meta.label}
      {requerido && (
        <span className="ml-0.5 text-danger" aria-hidden="true">
          *
        </span>
      )}
      {requerido && <span className="sr-only"> (obligatorio)</span>}
    </label>
  );

  const textos = (
    <>
      {ayuda !== '' && (
        <p id={idAyuda} className="mt-1 text-xs text-fg-faint">
          {ayuda}
        </p>
      )}
      {aclaracion !== '' && (
        <p id={idAclaracion} className="mt-1 text-xs text-fg-faint">
          {aclaracion}
        </p>
      )}
      {error !== null && (
        <p id={idError} role="alert" className="mt-1 text-xs font-medium text-danger">
          {error}
        </p>
      )}
    </>
  );

  // Booleano: la etiqueta va a la derecha del control y todo entra en una linea.
  if (widget === 'boolean' || widget === 'switch' || widget === 'checkbox') {
    return (
      <div className="min-w-0">
        <div className="flex items-center gap-2.5">
          {control}
          {etiqueta}
        </div>
        {textos}
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <div className="mb-1">{etiqueta}</div>
      {control}
      {textos}
    </div>
  );
}
